import { createFileRoute, notFound } from "@tanstack/react-router";
import { PageShell } from "@/components/site/PageShell";
import { GeoServicePage } from "@/components/site/GeoServicePage";
import { geoBreadcrumbJsonLd, geoServiceJsonLd } from "@/lib/jsonld-servicios";
import { getDistrito } from "@/data/geo";
import { seoMeta, canonicalLink, robotsMeta, defaultRobotsIndex } from "@/lib/seo";
import { applyServiceOverride, type ServiceSchemaData } from "@/lib/seo-schema-apply";

const SERVICE_NAME = "Promotoras BTL e impulsadoras";
const SERVICE_SLUG = "/impulsadoras-promotoras" as const;

const INCLUDES = [
  "Selección por perfil (presentación, experiencia, idiomas)",
  "Capacitación en producto, argumentos y protocolos",
  "Uniformes y briefing por activación",
  "Supervisor responsable en sitio",
  "Reporte de asistencia y desempeño",
];

const AUDIENCE = [
  "Marcas con activaciones o eventos por agendar",
  "Empresas con campañas de exhibición o muestreo",
  "Equipos que necesitan cobertura en varios puntos a la vez",
  "Lanzamientos que requieren atención presencial",
];

const BENEFITS = [
  { title: "Perfil correcto", desc: "Solo te presentamos candidatas que cumplen tu brief." },
  {
    title: "Equipo capacitado",
    desc: "Capacitación previa en tu producto y protocolos de marca.",
  },
  { title: "Supervisión real", desc: "Supervisor en sitio para cada activación o evento." },
];

export const Route = createFileRoute("/impulsadoras-promotoras/$distrito")({
  loader: ({ params }) => {
    const distrito = getDistrito(params.distrito);
    if (!distrito) throw notFound();
    return { distrito };
  },
  head: ({ loaderData, match }) => {
    const distrito = loaderData?.distrito;
    if (!distrito) return {};
    const path = `${SERVICE_SLUG}/${distrito.slug}`;
    const override = match.context.seoOverride;
    return {
      meta: [
        ...seoMeta({
          title:
            override?.metaTitle ?? `Promotoras BTL en ${distrito.name}, Lima | Improve Yourself`,
          description:
            override?.metaDescription ??
            `Promotoras BTL e impulsadoras en ${distrito.name}, Lima. Personal seleccionado por perfil, capacitado en tu producto y supervisado en sitio. Propuesta en 72 h.`,
          path,
          image: override?.ogImage ?? undefined,
        }),
        ...robotsMeta(
          override?.robotsIndex ?? defaultRobotsIndex(path),
          override?.robotsFollow ?? true,
        ),
      ],
      links: canonicalLink(override?.canonicalPath ?? path),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            applyServiceOverride(
              geoServiceJsonLd(SERVICE_NAME, SERVICE_SLUG, distrito),
              override?.schemaType === "service"
                ? (override.schemaData as Partial<ServiceSchemaData>)
                : null,
            ),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(geoBreadcrumbJsonLd(SERVICE_NAME, SERVICE_SLUG, distrito)),
        },
      ],
    };
  },
  component: ImpulsadorasPromotorasDistrito,
});

function ImpulsadorasPromotorasDistrito() {
  const { distrito } = Route.useLoaderData();
  return (
    <PageShell>
      <GeoServicePage
        slug={SERVICE_SLUG}
        serviceName={SERVICE_NAME}
        distrito={distrito}
        includes={INCLUDES}
        audience={AUDIENCE}
        benefits={BENEFITS}
      />
    </PageShell>
  );
}
